import React, { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import {
    Phone,
    MessageSquare,
    Check,
    ChevronDown,
    ChevronsLeftRight,
    Droplets,
    ListChecks,
    Handshake,
    Tag,
    Instagram,
    Facebook,
    Upload,
} from "lucide-react";
import "@/App.css";

/* ============================================================================
   PARKER WINDOW CLEANING — BUSINESS CONFIG
   Every business value on this page lives here. Change it once, it updates
   everywhere — header, hero, CTAs, sticky bar, footer, schema copy.
   ============================================================================ */
const CONFIG = {
    // --- Identity ---
    BUSINESS_NAME: "Parker Window Cleaning",
    OWNER: 'Zachary "Zach" Parker',
    DOMAIN: "https://zach-parker.com",

    // --- Phone: the ONLY number allowed anywhere on this page ---
    PHONE_DISPLAY: "(760) 237-0176",
    PHONE_TEL: "tel:+17602370176",
    PHONE_SMS: "sms:+17602370176",

    // --- Geography (every title / footer needs a California geo modifier) ---
    PRIMARY_CITY: "Oceanside, CA",
    REGION: "North County San Diego",
    CITIES: ["Oceanside", "Carlsbad", "Vista", "Encinitas", "San Marcos", "Escondido"],

    // --- Hours (must match Google Business Profile exactly) ---
    HOURS: "Monday–Saturday, 7:00am–6:00pm",

    // --- Residential pricing tiers (ranges; deliberately loose) ---
    PRICING: [
        { tier: "Small home", range: "$150–$200" },
        { tier: "Medium home", range: "$200–$300" },
        { tier: "Large home", range: "$300–$450+" },
    ],

    // --- Social profiles ---
    SOCIAL: {
        instagram: "https://instagram.com/zachparkerwindows",
        facebook: "https://facebook.com/zachparkerwindows",
        tiktok: "https://tiktok.com/@zachparkerwindows",
    },

    // --- Brand assets ---
    LOGO_SRC: "/images/LOGO-PRIMARY-blue-badge.jpg",
    LOGO_WEBP: "/images/LOGO-PRIMARY-blue-badge.webp",
    FAVICON: "/favicon.svg", // solid navy tile with white "P" — swap when a real mark exists

    // --- Integrations ---
    // Form posts to our own backend, which emails the request to the owner.
    // To move to GoHighLevel later, replace this URL with the CRM webhook.
    FORM_ENDPOINT: `${process.env.REACT_APP_BACKEND_URL}/api/quote`,
    ANALYTICS_ID: "",  // GA4/GTM measurement ID — when set, gtag loads and events flow.

    // --- Reviews: brand-new business, none yet. Flip to true + fill REVIEWS later. ---
    SHOW_REVIEWS: false,
    REVIEWS: [],
    // GOOGLE_REVIEW_LINK: "" // Google Business Profile review link goes here once reviews exist.

    /* --- Photo manifest (§12). Only real supplied job photos may appear.
       To enable a slot: drop the named file into /public/images/, then set
       available: true. Slots with available: false are dropped from the page
       entirely — no stock, no placeholders. --- */
    IMAGES: {
        hero: {
            available: true,
            src: "/images/05-AFTER-interior-slider-patio.jpg",
            webp: "/images/05-AFTER-interior-slider-patio.webp",
            alt: "View from inside a home through a spotless sliding glass door to a sunlit patio and lawn",
        },
        sliderBefore: {
            available: true,
            src: "/images/01-BEFORE-pool-view-salt-spray.jpg",
            webp: "/images/01-BEFORE-pool-view-salt-spray.webp",
            aspect: "991 / 1474", // exported identical to the AFTER frame or the comparison breaks
            alt: "Apartment window heavily coated in coastal salt spray and grime, obscuring the pool courtyard behind it",
        },
        sliderAfter: {
            available: true,
            src: "/images/01-AFTER-pool-view-clean.jpg",
            webp: "/images/01-AFTER-pool-view-clean.webp",
            aspect: "991 / 1474",
            alt: "The same window after cleaning — the pool courtyard, umbrella and planters are sharp and clear",
        },
        sideBySideBefore: {
            available: true,
            src: "/images/02-BEFORE-corner-windows-hazed.jpg",
            webp: "/images/02-BEFORE-corner-windows-hazed.webp",
            alt: "Corner window panes clouded with a heavy film of dirt and hard water spotting",
        },
        sideBySideAfter: {
            available: true,
            src: "/images/02-AFTER-corner-windows-clean.jpg",
            webp: "/images/02-AFTER-corner-windows-clean.webp",
            alt: "The same corner windows after cleaning, with clean frames and clear glass",
        },
        poolAlt: {
            available: true,
            src: "/images/01b-AFTER-pool-view-alt.jpg",
            webp: "/images/01b-AFTER-pool-view-alt.webp",
            alt: "Clear view of a condo pool and patio through freshly cleaned glass",
        },
        galleryPalms: {
            available: true,
            src: "/images/04-AFTER-interior-parking-palms-view.jpg",
            webp: "/images/04-AFTER-interior-parking-palms-view.webp",
            alt: "Clean interior window looking out over palm trees and a coastal North County street",
        },
        equipment: {
            available: true,
            src: "/images/03-PROCESS-water-fed-pole-upper-story.jpg",
            webp: "/images/03-PROCESS-water-fed-pole-upper-story.webp",
            alt: "A water-fed extension pole with a brush head reaching an upper-story window from the ground",
        },
        servicesFeatured: {
            available: true,
            src: "/images/06-AFTER-exterior-slider.jpg",
            webp: "/images/06-AFTER-exterior-slider.webp",
            alt: "Exterior of a large three-panel sliding glass door after cleaning, reflecting the garden",
        },
        includesGarden: {
            available: true,
            src: "/images/07-AFTER-interior-double-hung-garden.jpg",
            webp: "/images/07-AFTER-interior-double-hung-garden.webp",
            alt: "Two double-hung windows cleaned inside and out, looking onto a landscaped backyard",
        },
    },
};

/* --- Tracking: never errors when no analytics script is loaded --- */
const track = (event, data = {}) => {
    try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event, ...data });
        if (typeof window.gtag === "function") window.gtag("event", event, data);
    } catch {
        /* analytics not loaded — page must never break */
    }
};

/* --- Section scroll reveal (disabled under prefers-reduced-motion) --- */
const Reveal = ({ children, delay = 0, className = "" }) => {
    const reduce = useReducedMotion();
    if (reduce) return <div className={className}>{children}</div>;
    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.div>
    );
};

/* --- Numbered chapter label above section headings --- */
const Chapter = ({ num, label, light = false }) => (
    <p
        className={`font-display text-xs font-semibold uppercase tracking-[0.28em] mb-4 ${
            light ? "text-cream/80" : "text-sky"
        }`}
    >
        {num} — {label}
    </p>
);

/* --- Primary call CTA button (identical everywhere it appears) --- */
const CallCta = ({ className = "", testid }) => (
    <a
        data-testid={testid}
        href={CONFIG.PHONE_TEL}
        onClick={() => track("call_click", { location: testid })}
        className={`inline-flex items-center justify-center gap-3 min-h-[56px] px-8 rounded-[10px] bg-brand text-white font-display font-semibold text-lg tracking-wide transition-[background-color,transform] duration-200 hover:bg-navy active:scale-[0.98] ${className}`}
    >
        <Phone className="w-5 h-5" aria-hidden="true" />
        Call or Text {CONFIG.PHONE_DISPLAY}
    </a>
);

/* --- Final CTA band: shown once, just before the quote form --- */
const CtaBand = ({ testid }) => (
    <section className="bg-navy" data-testid={`${testid}-band`}>
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
            <Reveal>
                <h2 className="text-white font-display font-bold text-3xl sm:text-4xl lg:text-5xl">
                    Ready for windows you can actually see through?
                </h2>
                <p className="mt-5 text-cream/90 text-base md:text-lg max-w-2xl mx-auto">
                    Free estimates. Same-week scheduling. Screens and tracks included, always.
                    Locally owned, serving Oceanside and all of North County.
                </p>
                <div className="mt-9">
                    <CallCta testid={`${testid}-call-cta`} />
                </div>
                <a
                    data-testid={`${testid}-form-link`}
                    href="#quote-form"
                    className="mt-6 inline-block text-cream/80 underline underline-offset-4 decoration-cream/40 hover:text-cream transition-colors text-sm"
                >
                    Or send a few details and we'll call you back →
                </a>
            </Reveal>
        </div>
    </section>
);

/* --- TikTok icon (not in lucide) --- */
const TikTokIcon = ({ className = "w-5 h-5" }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M16.6 3c.4 2.1 1.9 3.7 4.1 3.9v3.1c-1.6 0-3-.5-4.1-1.3v6.9c0 3.9-2.6 6.4-6.2 6.4-3.4 0-6-2.6-6-6.1 0-3.7 3-6.3 6.9-6.1v3.2c-1.9-.3-3.7.9-3.7 3 0 1.7 1.3 2.9 2.9 2.9 1.8 0 2.9-1.4 2.9-3.4V3h3.2z" />
    </svg>
);

/* ============================================================================
   HEADER — sticky, all breakpoints
   ============================================================================ */
const Header = () => (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/95 backdrop-blur-sm border-b border-ink/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 md:h-20 flex items-center justify-between gap-4">
            <a href="#top" data-testid="header-brand" className="flex items-center gap-3 min-w-0">
                <picture>
                    <source srcSet={CONFIG.LOGO_WEBP} type="image/webp" />
                    <img
                        src={CONFIG.LOGO_SRC}
                        alt="Parker Window Cleaning logo — round blue badge with house and squeegee"
                        className="w-10 h-10 md:w-11 md:h-11 rounded-full shrink-0"
                        width="44"
                        height="44"
                    />
                </picture>
                <span className="min-w-0">
                    <span className="block font-display font-bold text-navy text-base md:text-lg leading-tight truncate">
                        {CONFIG.BUSINESS_NAME}
                    </span>
                    <span className="hidden md:block text-xs text-teal leading-tight">
                        Oceanside &amp; North County San Diego
                    </span>
                </span>
            </a>
            <a
                data-testid="header-call-button"
                href={CONFIG.PHONE_TEL}
                onClick={() => track("call_click", { location: "header" })}
                className="inline-flex items-center gap-2 min-h-[48px] px-4 sm:px-6 rounded-[10px] bg-brand text-white font-display font-semibold text-sm sm:text-base transition-[background-color,transform] duration-200 hover:bg-navy active:scale-[0.98] shrink-0"
            >
                <Phone className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline">{CONFIG.PHONE_DISPLAY}</span>
                <span className="sm:hidden">Call</span>
            </a>
        </div>
    </header>
);

/* ============================================================================
   HERO — kinetic masked line reveal, subtle parallax, brand rings
   ============================================================================ */
const HeroLine = ({ children, delay }) => {
    const reduce = useReducedMotion();
    if (reduce) return <span className="block">{children}</span>;
    return (
        <span className="block overflow-hidden pb-1">
            <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
            >
                {children}
            </motion.span>
        </span>
    );
};

const Hero = () => {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);
    const img = CONFIG.IMAGES.hero;

    return (
        <section
            ref={ref}
            id="top"
            data-testid="hero"
            className="relative overflow-hidden bg-navy pt-24 md:pt-32 pb-14 md:pb-20"
        >
            {img.available && (
                <>
                    <picture>
                        <source srcSet={img.webp} type="image/webp" />
                        <img
                            src={img.src}
                            alt={img.alt}
                            className="absolute inset-0 w-full h-full object-cover"
                            fetchPriority="high"
                        />
                    </picture>
                    <div className="absolute inset-0 bg-gradient-to-b from-navy/90 via-navy/70 to-navy/55" aria-hidden="true" />
                </>
            )}
            {/* Decorative badge rings echoing the logo — calm, on-brand geometry */}
            <div className="hero-ring w-[560px] h-[560px] -right-40 -top-40" aria-hidden="true" />
            <div className="hero-ring w-[380px] h-[380px] -right-20 -top-20 border-sky/20" aria-hidden="true" />
            <div className="hero-ring w-[720px] h-[720px] -left-72 -bottom-96 border-sky/15" aria-hidden="true" />

            <motion.div style={{ y }} className="relative max-w-6xl mx-auto px-6">
                <h1 className="text-white font-display font-bold text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
                    <HeroLine delay={0.1}>Spotless Windows in</HeroLine>
                    <HeroLine delay={0.22}>
                        Oceanside — <span className="text-cream">Screens and</span>
                    </HeroLine>
                    <HeroLine delay={0.34}>
                        <span className="text-cream">Tracks Included</span>
                    </HeroLine>
                </h1>
                <Reveal delay={0.5}>
                    <p className="mt-5 text-white/85 text-base md:text-lg max-w-xl">
                        Hand-washed glass inside and out. Basic screen and track cleaning, plus
                        frames and sills, with every service at no extra cost. Owner-operated by
                        Zach Parker.
                    </p>
                </Reveal>
                <Reveal delay={0.62}>
                    <div className="mt-7">
                        <CallCta testid="hero-call-cta" />
                    </div>
                    <p className="mt-4 text-white/70 text-sm">
                        Free estimates · No upsells · Serving all of North County
                    </p>
                </Reveal>
                <Reveal delay={0.74}>
                    <ul data-testid="hero-trust-chips" className="mt-8 flex flex-wrap gap-2.5">
                        {[
                            "3+ years experience",
                            "Locally owned & operated",
                            "Screens & tracks included",
                            "Satisfaction guaranteed",
                        ].map((chip) => (
                            <li
                                key={chip}
                                className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm text-white"
                            >
                                <Check className="w-4 h-4 text-gold" aria-hidden="true" />
                                {chip}
                            </li>
                        ))}
                    </ul>
                </Reveal>
            </motion.div>
        </section>
    );
};

/* ============================================================================
   PROBLEM STRIP
   ============================================================================ */
const ProblemStrip = () => (
    <section className="bg-white" data-testid="problem-strip">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
            <Reveal>
                <Chapter num="01" label="The honest bit" />
                <h2 className="font-display font-bold text-navy text-3xl sm:text-4xl">
                    You've been meaning to get to those windows.
                </h2>
                <p className="mt-5 text-teal text-base md:text-lg">
                    The ladder's in the garage. The squeegee leaves streaks. The screens haven't
                    come off in years, and the tracks — nobody wants to talk about the tracks.
                </p>
                <p className="mt-4 text-ink text-base md:text-lg font-medium">
                    Hand it to someone who does this every day, and gets the screens and tracks too.
                </p>
            </Reveal>
        </div>
    </section>
);

/* ============================================================================
   EVERY JOB INCLUDES — the centerpiece checklist
   ============================================================================ */
const Includes = () => {
    const items = [
        "Interior and exterior glass, hand-washed",
        "Screens removed, cleaned, and reinstalled",
        "Window tracks cleaned out",
        "Frames and sills wiped down",
        "Glass hand-dried and checked for streaks before we go",
        "Everything put back exactly where it was",
    ];
    const garden = CONFIG.IMAGES.includesGarden;
    return (
        <section className="bg-surface" data-testid="includes-section">
            <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
                <Reveal>
                    <Chapter num="02" label="No upcharge, ever" />
                    <div className={`grid gap-8 items-center ${garden.available ? "lg:grid-cols-[1.25fr_1fr]" : ""}`}>
                        <div className="bg-white rounded-2xl border border-ink/10 p-7 sm:p-10 shadow-[0_10px_40px_rgba(0,48,112,0.08)]">
                        <h2 className="font-display font-bold text-navy text-2xl sm:text-3xl">
                            Every Job Includes — No Upcharge
                        </h2>
                        <ul className="mt-7 space-y-4">
                            {items.map((item) => (
                                <li key={item} className="flex items-start gap-3">
                                    <span className="mt-0.5 w-6 h-6 rounded-full bg-gold/15 flex items-center justify-center shrink-0">
                                        <Check className="w-4 h-4 text-gold" aria-hidden="true" />
                                    </span>
                                    <span className="text-ink text-base md:text-lg">{item}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-8 text-sm text-teal">
                            Most companies charge per screen and extra for tracks. We don't. It's
                            part of the job.
                        </p>
                        <p className="mt-1 text-sm text-teal/80 italic">
                            Heavily built-up tracks and deep detailing are quoted separately.
                        </p>
                        </div>
                        {garden.available && (
                            <picture>
                                <source srcSet={garden.webp} type="image/webp" />
                                <img
                                    src={garden.src}
                                    alt={garden.alt}
                                    loading="lazy"
                                    className="w-full rounded-2xl border border-ink/10 object-cover aspect-[4/5] shadow-[0_16px_50px_rgba(0,48,112,0.12)]"
                                />
                            </picture>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

/* ============================================================================
   BEFORE / AFTER — interactive comparison slider (drag + tap + keyboard)
   ============================================================================ */
const CompareSlider = ({ before, after, aspect }) => {
    const [pos, setPos] = useState(50);
    const wrapRef = useRef(null);
    const dragging = useRef(false);

    const setFromClientX = (clientX) => {
        const el = wrapRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const pct = ((clientX - r.left) / r.width) * 100;
        setPos(Math.min(100, Math.max(0, pct)));
    };

    const onPointerDown = (e) => {
        dragging.current = true;
        wrapRef.current.setPointerCapture(e.pointerId);
        setFromClientX(e.clientX);
    };
    const onPointerMove = (e) => {
        if (dragging.current) setFromClientX(e.clientX);
    };
    const stop = () => {
        dragging.current = false;
    };

    return (
        <div
            ref={wrapRef}
            data-testid="ba-slider"
            className="ba-wrap relative w-full max-h-[78vh] rounded-2xl overflow-hidden border border-ink/10 shadow-[0_16px_60px_rgba(0,48,112,0.14)]"
            style={{ aspectRatio: aspect }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={stop}
            onPointerCancel={stop}
        >
            {/* AFTER = base layer */}
            <picture>
                <source srcSet={after.webp} type="image/webp" />
                <img src={after.src} alt={after.alt} className="absolute inset-0 w-full h-full object-cover" />
            </picture>
            {/* BEFORE = clipped top layer */}
            <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
                <picture>
                    <source srcSet={before.webp} type="image/webp" />
                    <img src={before.src} alt={before.alt} className="absolute inset-0 w-full h-full object-cover" />
                </picture>
            </div>
            <span className="absolute top-4 left-4 rounded-full bg-navy/85 text-white font-display text-xs font-semibold tracking-[0.18em] px-3 py-1.5">
                BEFORE
            </span>
            <span className="absolute top-4 right-4 rounded-full bg-gold text-navy font-display text-xs font-semibold tracking-[0.18em] px-3 py-1.5">
                AFTER
            </span>
            {/* Divider + grab handle */}
            <div className="ba-handle" style={{ left: `${pos}%` }} aria-hidden="true">
                <div className="ba-grip ba-handle-proxy">
                    <ChevronsLeftRight className="w-5 h-5" />
                </div>
            </div>
            {/* The real control: keyboard-operable, screen-reader announced */}
            <label className="visually-hidden" htmlFor="ba-range">
                Comparison position between before and after window cleaning photos
            </label>
            <input
                id="ba-range"
                data-testid="ba-range-input"
                type="range"
                min="0"
                max="100"
                step="1"
                value={Math.round(pos)}
                onChange={(e) => setPos(Number(e.target.value))}
                className="visually-hidden"
                aria-valuetext={`${Math.round(pos)} percent before visible`}
            />
        </div>
    );
};

const LabeledPhoto = ({ image, label, gold = false }) => (
    <figure className="relative rounded-2xl overflow-hidden border border-ink/10 shadow-[0_10px_40px_rgba(0,48,112,0.10)]">
        <picture>
            <source srcSet={image.webp} type="image/webp" />
            <img src={image.src} alt={image.alt} loading="lazy" className="w-full h-full object-cover aspect-[1284/1681]" />
        </picture>
        <span
            className={`absolute top-4 left-4 rounded-full font-display text-xs font-semibold tracking-[0.18em] px-3 py-1.5 ${
                gold ? "bg-gold text-navy" : "bg-navy/85 text-white"
            }`}
        >
            {label}
        </span>
    </figure>
);

const BeforeAfter = () => {
    const { sliderBefore, sliderAfter, sideBySideBefore, sideBySideAfter, poolAlt, galleryPalms } =
        CONFIG.IMAGES;
    if (!sliderBefore.available || !sliderAfter.available) return null;
    return (
        <section className="bg-white" data-testid="before-after-section">
            <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
                <Reveal className="text-center">
                    <Chapter num="03" label="Proof, not promises" />
                    <h2 className="font-display font-bold text-navy text-3xl sm:text-4xl lg:text-5xl">
                        Same window. Same day.
                    </h2>
                    <p className="mt-4 text-teal text-base md:text-lg max-w-2xl mx-auto">
                        Coastal salt spray builds up fast in North County. This is what comes off.
                    </p>
                    <p className="mt-2 text-sm text-ink/50">Drag, tap, or use arrow keys</p>
                </Reveal>
                <Reveal delay={0.1} className="mt-10 max-w-[800px] mx-auto">
                    <CompareSlider before={sliderBefore} after={sliderAfter} aspect={sliderBefore.aspect} />
                </Reveal>
                {sideBySideBefore.available && sideBySideAfter.available && (
                    <Reveal delay={0.05} className="mt-12 grid gap-6 sm:grid-cols-2 max-w-[800px] mx-auto">
                        <LabeledPhoto image={sideBySideBefore} label="BEFORE" />
                        <LabeledPhoto image={sideBySideAfter} label="AFTER" gold />
                    </Reveal>
                )}
                {(poolAlt.available || galleryPalms.available) && (
                    <Reveal delay={0.05} className="mt-12 max-w-[800px] mx-auto">
                        <p className="text-center font-display text-xs font-semibold uppercase tracking-[0.28em] text-teal mb-5">
                            More from real North County jobs
                        </p>
                        <div className="grid gap-6 sm:grid-cols-2">
                            {poolAlt.available && (
                                <picture>
                                    <source srcSet={poolAlt.webp} type="image/webp" />
                                    <img
                                        src={poolAlt.src}
                                        alt={poolAlt.alt}
                                        loading="lazy"
                                        className="w-full rounded-2xl border border-ink/10 object-cover aspect-[4/5]"
                                    />
                                </picture>
                            )}
                            {galleryPalms.available && (
                                <picture>
                                    <source srcSet={galleryPalms.webp} type="image/webp" />
                                    <img
                                        src={galleryPalms.src}
                                        alt={galleryPalms.alt}
                                        loading="lazy"
                                        className="w-full rounded-2xl border border-ink/10 object-cover aspect-[4/5]"
                                    />
                                </picture>
                            )}
                        </div>
                    </Reveal>
                )}
            </div>
        </section>
    );
};

/* ============================================================================
   SERVICES — five cards, first is featured
   ============================================================================ */
const Services = () => {
    const services = [
        {
            name: "Interior & Exterior — Full Service",
            desc: "The complete job. Glass inside and out, plus basic screen and track cleaning, frames and sills. Most homes are done in a single visit.",
            featured: true,
        },
        {
            name: "Exterior Only",
            desc: "Outside glass. Good for a refresh between full cleanings, or before you list or photograph the house.",
        },
        {
            name: "Hard Water Stain Removal",
            desc: "Sprinkler spotting and mineral buildup that regular cleaning won't touch. Priced after we take a look at it.",
        },
        {
            name: "Screen Repair",
            desc: "Torn, bent, or blown-out screens repaired and re-meshed. Most window cleaners won't touch this. We will.",
        },
        {
            name: "Storefront & Small Commercial",
            desc: "Small retail and office frontage on a schedule that suits you. Priced by estimate.",
        },
    ];
    const featured = services[0];
    const rest = services.slice(1);
    const featImg = CONFIG.IMAGES.servicesFeatured;

    return (
        <section className="bg-surface" data-testid="services-section">
            <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
                <Reveal>
                    <Chapter num="04" label="Services" />
                    <h2 className="font-display font-bold text-navy text-3xl sm:text-4xl">
                        What we do
                    </h2>
                </Reveal>
                <div className="mt-10 grid gap-6 md:grid-cols-2">
                    <Reveal className="md:col-span-2">
                        <div
                            data-testid="service-card-featured"
                            className="bg-navy text-white rounded-2xl p-8 sm:p-10 md:flex md:items-center md:gap-10"
                        >
                            <div className="flex-1">
                                <p className="font-display text-xs font-semibold uppercase tracking-[0.24em] text-gold mb-3">
                                    Most booked
                                </p>
                                <h3 className="font-display font-bold text-2xl sm:text-3xl">
                                    {featured.name}
                                </h3>
                                <p className="mt-4 text-white/85 text-base md:text-lg max-w-xl">
                                    {featured.desc}
                                </p>
                            </div>
                            {featImg.available && (
                                <picture>
                                    <source srcSet={featImg.webp} type="image/webp" />
                                    <img
                                        src={featImg.src}
                                        alt={featImg.alt}
                                        loading="lazy"
                                        className="mt-8 md:mt-0 w-full md:w-72 rounded-xl object-cover aspect-[4/3]"
                                    />
                                </picture>
                            )}
                        </div>
                    </Reveal>
                    {rest.map((s, i) => (
                        <Reveal key={s.name} delay={0.05 * (i + 1)}>
                            <div
                                data-testid={`service-card-${i + 2}`}
                                className="bg-white rounded-2xl border border-ink/10 p-7 h-full transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(0,48,112,0.10)]"
                            >
                                <h3 className="font-display font-semibold text-navy text-xl">
                                    {s.name}
                                </h3>
                                <p className="mt-3 text-teal text-base">{s.desc}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* ============================================================================
   PRICING — three tiers, ranges, no calculator
   ============================================================================ */
const Pricing = () => (
    <section className="bg-white" data-testid="pricing-section">
        <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
            <Reveal className="text-center">
                <Chapter num="05" label="Pricing" />
                <h2 className="font-display font-bold text-navy text-3xl sm:text-4xl">
                    Straightforward ranges
                </h2>
            </Reveal>
            <div className="mt-10 grid gap-5 sm:grid-cols-3">
                {CONFIG.PRICING.map((p, i) => (
                    <Reveal key={p.tier} delay={0.06 * i}>
                        <div
                            data-testid={`pricing-tier-${p.tier.split(" ")[0].toLowerCase()}`}
                            className="bg-surface rounded-2xl border border-ink/10 px-6 py-9 text-center h-full"
                        >
                            <p className="font-display text-xs font-semibold uppercase tracking-[0.22em] text-teal">
                                {p.tier}
                            </p>
                            <p className="mt-3 font-display font-bold text-navy text-3xl md:text-4xl">
                                {p.range}
                            </p>
                        </div>
                    </Reveal>
                ))}
            </div>
            <Reveal delay={0.15}>
                <p className="mt-8 text-center text-teal text-base max-w-2xl mx-auto">
                    Every home is quoted individually — window count, accessibility, and how much
                    detailing the tracks need all change the number. Call for a free estimate.
                </p>
                <div className="mt-8 text-center">
                    <CallCta testid="pricing-call-cta" />
                </div>
            </Reveal>
        </div>
    </section>
);

/* ============================================================================
   HOW IT WORKS — three numbered steps
   ============================================================================ */
const HowItWorks = () => {
    const steps = [
        {
            n: "1",
            title: "Call or text.",
            desc: "Tell us roughly how many windows and where you are. Most estimates take two minutes.",
        },
        {
            n: "2",
            title: "Pick your day.",
            desc: "We'll find a time that works — most jobs get scheduled the same week.",
        },
        {
            n: "3",
            title: "Walk it with us.",
            desc: "When we're done we walk the house with you. Anything you're not happy with, we fix before we leave.",
        },
    ];
    return (
        <section className="bg-surface" data-testid="how-it-works-section">
            <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
                <Reveal>
                    <Chapter num="06" label="How it works" />
                    <h2 className="font-display font-bold text-navy text-3xl sm:text-4xl">
                        Three steps, no runaround
                    </h2>
                </Reveal>
                <div className="mt-10 grid gap-6 md:grid-cols-3">
                    {steps.map((s, i) => (
                        <Reveal key={s.n} delay={0.07 * i}>
                            <div className="bg-white rounded-2xl border border-ink/10 p-7 h-full">
                                <span className="font-display font-bold text-5xl text-sky/25">
                                    {s.n}
                                </span>
                                <h3 className="mt-4 font-display font-semibold text-navy text-xl">
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-teal text-base">{s.desc}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* ============================================================================
   WHY CHOOSE — owner value props
   ============================================================================ */
const WhyChoose = () => {
    const blocks = [
        {
            icon: Droplets,
            title: "Professional equipment.",
            desc: "Water-fed poles reach upstairs windows from the ground — no ladders leaned against your house.",
            image: CONFIG.IMAGES.equipment,
        },
        {
            icon: ListChecks,
            title: "Attention to detail.",
            desc: "The parts other companies skip are the parts we're known for.",
        },
        {
            icon: Handshake,
            title: "Friendly and reliable.",
            desc: "You get Zach. Same person on the phone, same person at your door.",
        },
        {
            icon: Tag,
            title: "Quality work, fair prices.",
            desc: "Honest rates, quoted straight, with no add-on games.",
        },
    ];
    return (
        <section className="bg-white" data-testid="why-choose-section">
            <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
                <Reveal>
                    <Chapter num="07" label="Why Parker" />
                    <h2 className="font-display font-bold text-navy text-3xl sm:text-4xl">
                        Why choose Parker Window Cleaning
                    </h2>
                </Reveal>
                <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {blocks.map((b, i) => {
                        const Icon = b.icon;
                        return (
                            <Reveal key={b.title} delay={0.04 * i}>
                                <div className="bg-surface rounded-2xl border border-ink/10 p-6 h-full">
                                    <Icon className="w-7 h-7 text-sky" aria-hidden="true" />
                                    <h3 className="mt-4 font-display font-semibold text-navy text-lg leading-snug">
                                        {b.title}
                                    </h3>
                                    <p className="mt-2 text-teal text-sm md:text-base">{b.desc}</p>
                                    {b.image?.available && (
                                        <picture>
                                            <source srcSet={b.image.webp} type="image/webp" />
                                            <img
                                                src={b.image.src}
                                                alt={b.image.alt}
                                                loading="lazy"
                                                className="mt-5 rounded-xl w-full aspect-square object-cover"
                                            />
                                        </picture>
                                    )}
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

/* ============================================================================
   SERVICE AREA — text list, no map iframe
   ============================================================================ */
const ServiceArea = () => (
    <section className="bg-surface" data-testid="service-area-section">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-24 text-center">
            <Reveal>
                <Chapter num="08" label="Service area" />
                <h2 className="font-display font-bold text-navy text-3xl sm:text-4xl">
                    Serving North County San Diego
                </h2>
                <p className="mt-6 font-display font-medium text-teal text-lg md:text-xl leading-relaxed">
                    {CONFIG.CITIES.join(" · ")} · and surrounding North County
                </p>
                <p className="mt-5 text-teal/85 text-base">
                    Not sure if you're in our area? Give us a call — if we can't get to you, we'll
                    point you toward someone who can.
                </p>
            </Reveal>
        </div>
    </section>
);

/* ============================================================================
   REVIEWS — built, hidden until real reviews exist (SHOW_REVIEWS = false)
   ============================================================================ */
const Reviews = () => (
    <section className="bg-white" data-testid="reviews-section">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
            <Chapter num="09" label="Reviews" />
            <h2 className="font-display font-bold text-navy text-3xl sm:text-4xl">
                What neighbors say
            </h2>
            {/* TODO: add Google Business Profile review link here once reviews exist */}
            <div className="mt-10 grid gap-6 md:grid-cols-3">
                {CONFIG.REVIEWS.map((r, i) => (
                    <figure key={i} className="bg-surface rounded-2xl border border-ink/10 p-7">
                        <blockquote className="text-ink text-base">{r.text}</blockquote>
                        <figcaption className="mt-4 text-teal text-sm font-medium">
                            {r.name}
                        </figcaption>
                    </figure>
                ))}
            </div>
        </div>
    </section>
);

/* ============================================================================
   FAQ — accessible accordion
   ============================================================================ */
const FAQ = () => {
    const [open, setOpen] = useState(0);
    const faqs = [
        {
            q: "How much does it cost?",
            a: "Most small homes run $150–$200, medium homes $200–$300, and large homes $300–$450 and up. It depends on how many windows you have, how easy they are to reach, and how much the tracks need. Call or text and we'll give you a real number.",
        },
        {
            q: "Are screens and tracks really included?",
            a: "Yes. Basic screen cleaning, basic track cleaning, and a frame and sill wipe-down come with every full service at no extra cost. A lot of companies charge separately for those. We don't. If tracks are heavily built up and need deep detailing, we'll quote that separately and tell you before we start.",
        },
        {
            q: "Do I need to be home?",
            a: "For exterior-only, no. For interior work someone needs to let us in, but you don't have to hover — most people go about their day.",
        },
        {
            q: "How long does it take?",
            a: "Most homes take two to four hours. Bigger homes, or a first cleaning with heavy buildup, can run longer. We'll tell you what to expect when we quote it.",
        },
        {
            q: "How often should I have this done?",
            a: "Twice a year suits most North County homes. Close to the coast, salt air means you'll notice it sooner — a lot of coastal customers go three times a year.",
        },
        {
            q: "Do you repair screens too?",
            a: "Yes. Torn, bent, and blown-out screens get repaired and re-meshed, quoted by the job. Most window cleaners won't do it, so it's usually one more contractor to chase. Not here.",
        },
        {
            q: "What if it rains?",
            a: "We'll reschedule at no charge. Rain alone doesn't dirty clean glass — spotting comes from what's already on it — but nobody wants to watch it happen the day after.",
        },
    ];
    return (
        <section className="bg-white" data-testid="faq-section">
            <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
                <Reveal>
                    <Chapter num="09" label="FAQ" />
                    <h2 className="font-display font-bold text-navy text-3xl sm:text-4xl">
                        Straight answers
                    </h2>
                </Reveal>
                <div className="mt-8 divide-y divide-ink/10 border-y border-ink/10">
                    {faqs.map((f, i) => {
                        const isOpen = open === i;
                        return (
                            <div key={f.q}>
                                <button
                                    data-testid={`faq-toggle-${i}`}
                                    className="w-full flex items-center justify-between gap-4 py-5 text-left"
                                    aria-expanded={isOpen}
                                    aria-controls={`faq-panel-${i}`}
                                    onClick={() => setOpen(isOpen ? -1 : i)}
                                >
                                    <span className="font-display font-semibold text-navy text-lg">
                                        {f.q}
                                    </span>
                                    <ChevronDown
                                        className={`w-5 h-5 text-sky shrink-0 transition-transform duration-300 ${
                                            isOpen ? "rotate-180" : ""
                                        }`}
                                        aria-hidden="true"
                                    />
                                </button>
                                <div
                                    id={`faq-panel-${i}`}
                                    role="region"
                                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                                        isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                                    }`}
                                >
                                    <div className="overflow-hidden">
                                        <p className="pb-5 text-teal text-base">{f.a}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

/* ============================================================================
   QUOTE FORM — backup for people who won't call. One instance only.
   ============================================================================ */
const QuoteForm = () => {
    const [submitted, setSubmitted] = useState(false);
    const [sending, setSending] = useState(false);
    const [failed, setFailed] = useState(false);
    const mountedAt = useRef(Date.now());

    const onSubmit = async (e) => {
        e.preventDefault();
        const fd = new FormData(e.target);
        // Honeypot + time-on-page check: bots fail both. Silently "succeed".
        const isBot = fd.get("company") || Date.now() - mountedAt.current < 3000;
        track("quote_submit");
        if (isBot) {
            setSubmitted(true);
            return;
        }
        setSending(true);
        setFailed(false);
        try {
            const resp = await fetch(CONFIG.FORM_ENDPOINT, { method: "POST", body: fd });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            setSubmitted(true);
        } catch {
            setFailed(true);
        }
        setSending(false);
    };

    const inputCls =
        "w-full min-h-[48px] rounded-[10px] border border-ink/20 bg-white px-4 text-base text-ink placeholder:text-ink/40 transition-[border-color] focus:border-sky";

    return (
        <section id="quote-form" className="bg-surface" data-testid="quote-form-section">
            <div className="max-w-2xl mx-auto px-6 py-16 md:py-24">
                <Reveal>
                    <Chapter num="10" label="Or start here" />
                    <h2 className="font-display font-bold text-navy text-3xl sm:text-4xl">
                        Request a free estimate
                    </h2>
                    <p className="mt-3 text-teal text-base">
                        Rather talk it through?{" "}
                        <a
                            data-testid="form-phone-link"
                            href={CONFIG.PHONE_TEL}
                            onClick={() => track("call_click", { location: "quote-form" })}
                            className="text-brand font-semibold underline underline-offset-4 hover:text-navy"
                        >
                            Call or text {CONFIG.PHONE_DISPLAY}
                        </a>
                    </p>
                    {submitted ? (
                        <div
                            data-testid="quote-form-success"
                            role="status"
                            className="mt-8 bg-white rounded-2xl border border-sky/30 p-8 text-center"
                        >
                            <span className="mx-auto w-12 h-12 rounded-full bg-gold/15 flex items-center justify-center">
                                <Check className="w-6 h-6 text-gold" aria-hidden="true" />
                            </span>
                            <p className="mt-4 font-display font-semibold text-navy text-xl">
                                Got it. Zach will call you back today.
                            </p>
                        </div>
                    ) : (
                        <form
                            data-testid="quote-form"
                            onSubmit={onSubmit}
                            className="mt-8 bg-white rounded-2xl border border-ink/10 p-6 sm:p-8 space-y-5"
                        >
                            {/* Honeypot — invisible to humans, bots fill it */}
                            <div className="hp-field" aria-hidden="true">
                                <label htmlFor="qf-company">Company</label>
                                <input id="qf-company" name="company" type="text" tabIndex="-1" autoComplete="off" />
                            </div>
                            <div>
                                <label htmlFor="qf-name" className="block text-sm font-medium text-ink mb-1.5">
                                    Name
                                </label>
                                <input data-testid="qf-name" id="qf-name" name="name" type="text" required autoComplete="name" className={inputCls} />
                            </div>
                            <div>
                                <label htmlFor="qf-phone" className="block text-sm font-medium text-ink mb-1.5">
                                    Phone
                                </label>
                                <input data-testid="qf-phone" id="qf-phone" name="phone" type="tel" required autoComplete="tel" className={inputCls} />
                            </div>
                            <div>
                                <label htmlFor="qf-address" className="block text-sm font-medium text-ink mb-1.5">
                                    Address or ZIP
                                </label>
                                <input data-testid="qf-address" id="qf-address" name="address" type="text" required autoComplete="postal-code" className={inputCls} />
                            </div>
                            <div>
                                <label htmlFor="qf-windows" className="block text-sm font-medium text-ink mb-1.5">
                                    About how many windows?
                                </label>
                                <select data-testid="qf-windows" id="qf-windows" name="windows" required defaultValue="" className={inputCls}>
                                    <option value="" disabled>
                                        Choose one
                                    </option>
                                    <option>Under 10</option>
                                    <option>10–20</option>
                                    <option>20–30</option>
                                    <option>30+</option>
                                    <option>Not sure</option>
                                </select>
                            </div>
                            <div>
                                <label htmlFor="qf-photo" className="block text-sm font-medium text-ink mb-1.5">
                                    Photo of the windows <span className="text-ink/50">(optional)</span>
                                </label>
                                <label
                                    htmlFor="qf-photo"
                                    className="flex items-center gap-3 min-h-[48px] rounded-[10px] border border-dashed border-ink/25 px-4 text-sm text-teal cursor-pointer transition-colors hover:border-sky"
                                >
                                    <Upload className="w-4 h-4 text-sky" aria-hidden="true" />
                                    Tap to attach a photo
                                </label>
                                <input data-testid="qf-photo" id="qf-photo" name="photo" type="file" accept="image/*" className="visually-hidden" />
                            </div>
                            <div>
                                <label htmlFor="qf-message" className="block text-sm font-medium text-ink mb-1.5">
                                    Anything else? <span className="text-ink/50">(optional)</span>
                                </label>
                                <textarea data-testid="qf-message" id="qf-message" name="message" rows="3" className={`${inputCls} py-3`} />
                            </div>
                            <button
                                data-testid="quote-form-submit"
                                type="submit"
                                disabled={sending}
                                className="w-full min-h-[56px] rounded-[10px] bg-brand text-white font-display font-semibold text-lg transition-[background-color,transform] duration-200 hover:bg-navy active:scale-[0.99] disabled:opacity-60"
                            >
                                {sending ? "Sending…" : "Request My Free Estimate"}
                            </button>
                            {failed && (
                                <p data-testid="quote-form-error" role="alert" className="text-center text-sm text-red-700">
                                    Something went wrong on our end — please call or text{" "}
                                    <a href={CONFIG.PHONE_TEL} className="font-semibold underline underline-offset-2">
                                        {CONFIG.PHONE_DISPLAY}
                                    </a>{" "}
                                    instead.
                                </p>
                            )}
                        </form>
                    )}
                </Reveal>
            </div>
        </section>
    );
};

/* ============================================================================
   FOOTER — navy, geo modifier, no email
   ============================================================================ */
const Footer = () => (
    <footer className="bg-navy text-white" data-testid="footer">
        <div className="max-w-6xl mx-auto px-6 pt-14 pb-28 md:pb-12">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-10">
                <div className="max-w-sm">
                    <div className="flex items-center gap-3">
                        <picture>
                            <source srcSet={CONFIG.LOGO_WEBP} type="image/webp" />
                            <img
                                src={CONFIG.LOGO_SRC}
                                alt="Parker Window Cleaning logo — round blue badge with house and squeegee"
                                className="w-12 h-12 rounded-full"
                                width="48"
                                height="48"
                                loading="lazy"
                            />
                        </picture>
                        <div>
                            <p className="font-display font-bold text-lg leading-tight">
                                {CONFIG.BUSINESS_NAME} · Oceanside, CA
                            </p>
                            <p className="text-cream/70 text-sm">
                                Locally owned &amp; operated · 3+ years experience
                            </p>
                        </div>
                    </div>
                    <p className="mt-6 text-white/75 text-sm leading-relaxed">
                        Serving {CONFIG.CITIES.join(", ")}, and surrounding North County San Diego.
                    </p>
                </div>
                <div>
                    <p className="font-display text-xs font-semibold uppercase tracking-[0.24em] text-cream/70 mb-4">
                        Call or text
                    </p>
                    <a
                        data-testid="footer-phone-link"
                        href={CONFIG.PHONE_TEL}
                        onClick={() => track("call_click", { location: "footer" })}
                        className="font-display font-bold text-2xl text-white hover:text-cream transition-colors"
                    >
                        {CONFIG.PHONE_DISPLAY}
                    </a>
                    <p className="mt-3 text-white/75 text-sm">{CONFIG.HOURS}</p>
                    <div className="mt-6 flex items-center gap-3">
                        <a
                            data-testid="footer-instagram"
                            href={CONFIG.SOCIAL.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Parker Window Cleaning on Instagram"
                            className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center transition-colors hover:bg-white/20"
                        >
                            <Instagram className="w-5 h-5" />
                        </a>
                        <a
                            data-testid="footer-facebook"
                            href={CONFIG.SOCIAL.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Parker Window Cleaning on Facebook"
                            className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center transition-colors hover:bg-white/20"
                        >
                            <Facebook className="w-5 h-5" />
                        </a>
                        <a
                            data-testid="footer-tiktok"
                            href={CONFIG.SOCIAL.tiktok}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Parker Window Cleaning on TikTok"
                            className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center transition-colors hover:bg-white/20"
                        >
                            <TikTokIcon />
                        </a>
                    </div>
                </div>
            </div>
            <div className="mt-12 pt-6 border-t border-white/15 text-white/60 text-sm">
                © {new Date().getFullYear()} {CONFIG.BUSINESS_NAME} · Oceanside, CA · North County
                San Diego
            </div>
        </div>
    </footer>
);

/* ============================================================================
   MOBILE STICKY BAR — < 768px only, always visible
   ============================================================================ */
const StickyBar = () => (
    <div
        data-testid="mobile-sticky-bar"
        className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-ink/15 shadow-[0_-6px_24px_rgba(0,48,112,0.12)] px-3 pt-3"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
    >
        <div className="flex gap-3">
            <a
                data-testid="sticky-call-button"
                href={CONFIG.PHONE_TEL}
                onClick={() => track("call_click", { location: "sticky-bar" })}
                className="flex-[7] inline-flex items-center justify-center gap-2 min-h-[56px] rounded-[10px] bg-gold text-navy font-display font-bold text-lg transition-[background-color,transform] active:scale-[0.98]"
            >
                <Phone className="w-5 h-5" aria-hidden="true" />
                Call Now
            </a>
            <a
                data-testid="sticky-text-button"
                href={CONFIG.PHONE_SMS}
                onClick={() => track("text_click", { location: "sticky-bar" })}
                className="flex-[3] inline-flex items-center justify-center gap-2 min-h-[56px] rounded-[10px] bg-navy text-white font-display font-semibold text-base transition-[background-color,transform] active:scale-[0.98]"
            >
                <MessageSquare className="w-4 h-4" aria-hidden="true" />
                Text Us
            </a>
        </div>
    </div>
);

/* ============================================================================
   APP — single page, anchor links only
   ============================================================================ */
function App() {
    // Smooth momentum scrolling (disabled for reduced motion)
    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const lenis = new Lenis({ duration: 1.05 });
        let rafId;
        const raf = (t) => {
            lenis.raf(t);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);
        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
        };
    }, []);

    // Analytics: loads only when ANALYTICS_ID is set in the config above
    useEffect(() => {
        if (!CONFIG.ANALYTICS_ID) return;
        const s = document.createElement("script");
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${CONFIG.ANALYTICS_ID}`;
        document.head.appendChild(s);
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () {
            window.dataLayer.push(arguments);
        };
        window.gtag("js", new Date());
        window.gtag("config", CONFIG.ANALYTICS_ID);
    }, []);

    return (
        <div className="App">
            <Header />
            <main className="pb-24 md:pb-0">
                <Hero />
                <ProblemStrip />
                <Includes />
                <BeforeAfter />
                <Services />
                <Pricing />
                <HowItWorks />
                <WhyChoose />
                <ServiceArea />
                {CONFIG.SHOW_REVIEWS && <Reviews />}
                <FAQ />
                <CtaBand testid="cta-final" />
                <QuoteForm />
            </main>
            <Footer />
            <StickyBar />
        </div>
    );
}

export default App;
